import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { EventType } from "../types";
import { defaultSchedule } from "../config/schedule";
import { AdminShell } from "../components/admin/AdminShell";
import { TimeSelect } from "../components/admin/TimeSelect";
import { Toast } from "../components/Toast";
import { useConfirm } from "../hooks/useConfirm";
import { useToast } from "../hooks/useToast";
import { buildTimeRange, minutesToTime, timeToMinutes } from "../utils/date";
import { getScheduleStartForDate, summarizeClosedDates } from "../utils/events";
import { buildDateRange } from "../utils/ranges";

export function AdminAvailabilityPage() {
  const [events, setEvents] = useState<EventType[]>([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [availabilityScope, setAvailabilityScope] = useState<"event" | "regular" | "portrait" | "all">("regular");
  const [manageDate, setManageDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [closeRangeStart, setCloseRangeStart] = useState(dayjs().format("YYYY-MM-DD"));
  const [closeRangeEnd, setCloseRangeEnd] = useState(dayjs().format("YYYY-MM-DD"));
  const [blockStartTime, setBlockStartTime] = useState("09:00");
  const [blockEndTime, setBlockEndTime] = useState("10:00");
  const { toast, showToast } = useToast();
  const { askConfirm, confirmModal } = useConfirm();

  const selectedEvent = events.find((event) => event.id === selectedEventId) || events[0];

  const refresh = useCallback(() => {
    api.getEvents(true).then((items) => {
      setEvents(items);
      if (!selectedEventId && items[0]) setSelectedEventId(items[0].id);
    });
  }, [selectedEventId]);

  useEffect(refresh, [refresh]);

  useEffect(() => {
    if (!selectedEvent) return;
    const startTime = getScheduleStartForDate(selectedEvent, manageDate);
    const endTime = minutesToTime(Math.min(timeToMinutes(startTime) + Math.max(selectedEvent.durationMinutes || selectedEvent.schedule.slotInterval || 30, 30), 24 * 60));
    setBlockStartTime(startTime);
    setBlockEndTime(endTime === "24:00" ? "23:00" : endTime);
  }, [selectedEvent, manageDate]);

  async function patchSelected(patch: Partial<EventType>) {
    if (!selectedEvent) return;
    await Promise.all(getAvailabilityTargets().map((event) => api.updateEvent(event.id, patch)));
    refresh();
  }

  function getAvailabilityTargets() {
    if (!selectedEvent) return [];
    if (availabilityScope === "event") return [selectedEvent];
    if (availabilityScope === "all") return events;
    return events.filter((event) => event.category === availabilityScope);
  }

  function availabilityScopeLabel() {
    if (availabilityScope === "event") return "зөвхөн энэ багц";
    if (availabilityScope === "all") return "бүх багц";
    return `${availabilityScope === "portrait" ? "Portrait" : "Regular"} бүх багц`;
  }

  function weeklyHoursFor(event: EventType) {
    return event.schedule.weeklyHours || defaultSchedule.weeklyHours || {};
  }

  async function updateWeeklyHour(day: number, patch: Partial<{ open: boolean; startTime: string; endTime: string }>) {
    if (!selectedEvent) return;
    const weeklyHours = weeklyHoursFor(selectedEvent);
    const current = weeklyHours[String(day)] || { open: selectedEvent.schedule.weekdays.includes(day), startTime: selectedEvent.schedule.startTime, endTime: selectedEvent.schedule.endTime };
    const nextWeeklyHours = {
      ...weeklyHours,
      [String(day)]: { ...current, ...patch },
    };
    const nextWeekdays = Object.entries(nextWeeklyHours)
      .filter(([, value]) => value.open)
      .map(([key]) => Number(key));
    await patchSelected({ schedule: { ...selectedEvent.schedule, weeklyHours: nextWeeklyHours, weekdays: nextWeekdays } });
  }

  function updateDateRangeClosed(close: boolean) {
    if (!selectedEvent) return;
    const dates = buildDateRange(closeRangeStart, closeRangeEnd);
    if (!dates.length) {
      showToast("Өдрийн range буруу байна.");
      return;
    }
    const action = close ? "хаах" : "нээх";
    askConfirm({
      title: `Өдрүүд ${action} уу?`,
      message: `${availabilityScopeLabel()} дээр ${dates[0]} - ${dates[dates.length - 1]} хооронд ${dates.length} өдрийг ${action} гэж байна.`,
      confirmLabel: action,
      onConfirm: async () => {
        const rangeSet = new Set(dates);
        const currentClosed = selectedEvent.schedule.closedDates || [];
        const schedule = {
          ...selectedEvent.schedule,
          closedDates: close
            ? Array.from(new Set([...currentClosed, ...dates])).sort()
            : currentClosed.filter((date) => !rangeSet.has(date)),
        };
        await patchSelected({ schedule });
        showToast(`${dates.length} өдөр ${action} үйлдэл ${availabilityScopeLabel()} дээр хийгдлээ.`);
      },
    });
  }

  function updateBlockedTimeRange(close: boolean) {
    if (!selectedEvent) return;
    const times = buildTimeRange(blockStartTime, blockEndTime, selectedEvent.schedule.slotInterval || selectedEvent.durationMinutes || 30);
    if (!times.length) {
      showToast("Цагийн range буруу байна.");
      return;
    }
    const current = selectedEvent.schedule.blockedSlots[manageDate] || [];
    const action = close ? "хаах" : "нээх";
    askConfirm({
      title: `Цагийн range ${action} уу?`,
      message: `${availabilityScopeLabel()} дээр ${manageDate} өдрийн ${blockStartTime} - ${blockEndTime} хооронд ${times.length} цагийг ${action} гэж байна.`,
      confirmLabel: action,
      onConfirm: async () => {
        const timeSet = new Set(times);
        const schedule = {
          ...selectedEvent.schedule,
          blockedSlots: {
            ...selectedEvent.schedule.blockedSlots,
            [manageDate]: close
              ? Array.from(new Set([...current, ...times])).sort()
              : current.filter((time) => !timeSet.has(time)),
          },
        };
        await patchSelected({ schedule });
        showToast(`${times.length} цаг ${action} үйлдэл ${availabilityScopeLabel()} дээр хийгдлээ.`);
      },
    });
  }

  return (
    <AdminShell active="availability" title="Availability">
      <section className="availability-page-card admin-card">
        <h2>Weekly hours</h2>
        <p className="muted">Set when you are typically available for meetings</p>
        {selectedEvent && (
          <>
            <select value={selectedEvent.id} onChange={(e) => setSelectedEventId(e.target.value)}>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
            <label className="availability-scope">
              <span>Хаалт хийх хүрээ</span>
              <select value={availabilityScope} onChange={(e) => setAvailabilityScope(e.target.value as "event" | "regular" | "portrait" | "all")}>
                <option value="event">Зөвхөн энэ багц</option>
                <option value="regular">Regular бүх багц</option>
                <option value="portrait">Portrait бүх багц</option>
                <option value="all">Бүх багц</option>
              </select>
            </label>
            <div className="weekly-hours-list">
              {[0, 1, 2, 3, 4, 5, 6].map((dayValue) => {
                const labels: Record<number, string> = { 0: "S", 1: "M", 2: "T", 3: "W", 4: "T", 5: "F", 6: "S" };
                const current = weeklyHoursFor(selectedEvent)[String(dayValue)] || {
                  open: selectedEvent.schedule.weekdays.includes(dayValue),
                  startTime: selectedEvent.schedule.startTime,
                  endTime: selectedEvent.schedule.endTime,
                };
                return (
                  <div className="weekly-hour-row" key={dayValue}>
                    <button
                      className={current.open ? "day-badge active" : "day-badge"}
                      type="button"
                      onClick={() => updateWeeklyHour(dayValue, { open: !current.open })}
                    >
                      {labels[dayValue]}
                    </button>
                    <TimeSelect value={current.startTime} disabled={!current.open} onChange={(value) => updateWeeklyHour(dayValue, { startTime: value })} />
                    <span>-</span>
                    <TimeSelect value={current.endTime} disabled={!current.open} onChange={(value) => updateWeeklyHour(dayValue, { endTime: value })} />
                    <button type="button" onClick={() => updateWeeklyHour(dayValue, { open: false })}>×</button>
                    <button type="button" onClick={() => updateWeeklyHour(dayValue, { open: true })}>＋</button>
                  </div>
                );
              })}
            </div>
            <p className="timezone-label">Ulaanbaatar Time⌄</p>
            <div className="availability-section">
              <strong>Өдөр хаах/нээх</strong>
              <div className="date-range-picker">
                <label>
                  <span>Эхлэх өдөр</span>
                  <input type="date" value={closeRangeStart} onChange={(e) => setCloseRangeStart(e.target.value)} />
                </label>
                <label>
                  <span>Дуусах өдөр</span>
                  <input type="date" value={closeRangeEnd} onChange={(e) => setCloseRangeEnd(e.target.value)} />
                </label>
              </div>
              <div className="availability-actions">
                <button type="button" onClick={() => updateDateRangeClosed(true)}>
                  Энэ range-г хаах
                </button>
                <button type="button" onClick={() => updateDateRangeClosed(false)}>
                  Энэ range-г нээх
                </button>
              </div>
              <p className="muted">
                Хаасан өдрүүд: {summarizeClosedDates(selectedEvent.schedule.closedDates)}
              </p>
            </div>
            <div className="availability-section">
              <strong>Тухайн өдрийн цаг хаах/нээх</strong>
              <input type="date" value={manageDate} onChange={(e) => setManageDate(e.target.value)} />
              <div className="date-range-picker">
                <label>
                  <span>Эхлэх цаг</span>
                  <TimeSelect value={blockStartTime} onChange={setBlockStartTime} />
                </label>
                <label>
                  <span>Дуусах цаг</span>
                  <TimeSelect value={blockEndTime} onChange={setBlockEndTime} />
                </label>
              </div>
              <div className="availability-actions">
                <button type="button" onClick={() => updateBlockedTimeRange(true)}>
                  Энэ цагийн range-г хаах
                </button>
                <button type="button" onClick={() => updateBlockedTimeRange(false)}>
                  Энэ цагийн range-г нээх
                </button>
              </div>
            <p className="muted">
              Хаасан цагууд: {(selectedEvent.schedule.blockedSlots[manageDate] || []).join(", ") || "байхгүй"}
            </p>
            </div>
          </>
        )}
      </section>
      {confirmModal}
      <Toast message={toast} />
    </AdminShell>
  );
}
